import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Spinner, EmptyState } from "@/components/ui";
import Modal from "@/components/ui/Modal";
import toast from "react-hot-toast";
import clsx from "clsx";

/* ─── Type config ──────────────────────────────────────────────────────────── */
const TYPES = [
  { id: "school",  label: "Schule",          color: "#8b5cf6", bg: "rgba(139,92,246,0.12)", desc: "Volksschule, Mittelschule, Gymnasium…",
    Icon: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
  { id: "center",  label: "Förderzentrum",   color: "#06b6d4", bg: "rgba(6,182,212,0.12)", desc: "Lernförderung, Integration…",
    Icon: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.05 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.21 9.77a16 16 0 0 0 6 6l1.13-1.09a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z"/></svg> },
  { id: "youth",   label: "Jugendzentrum",   color: "#22c55e", bg: "rgba(34,197,94,0.12)", desc: "Nachmittagsbetreuung, Sozialarbeit…",
    Icon: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  { id: "therapy", label: "Therapiezentrum", color: "#f59e0b", bg: "rgba(245,158,11,0.12)", desc: "Ergotherapie, Logopädie, Psychologie…",
    Icon: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
];
const TYPE_MAP = Object.fromEntries(TYPES.map((t) => [t.id, t]));

/* ─── Institution card ──────────────────────────────────────────────────────── */
function InstitutionCard({ inst, onEdit, onDelete }) {
  const t = TYPE_MAP[inst.type] || TYPES[0];
  return (
    <div className="group flex items-start gap-3 p-4 rounded-2xl border transition-all duration-200 hover:border-white/10"
      style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.05)" }}>
      <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: t.bg, color: t.color }}>
        <t.Icon/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <p className="font-bold text-sm text-white truncate flex-1">{inst.name}</p>
          <span className="flex-shrink-0 text-[9px] font-black px-2 py-0.5 rounded-full" style={{ background: t.bg, color: t.color }}>
            {t.label}
          </span>
        </div>
        {inst.city && (
          <p className="text-[11px] text-gray-600 mt-0.5 flex items-center gap-1">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {inst.city}
          </p>
        )}
        {inst.address && <p className="text-[10px] text-gray-700 mt-0.5">{inst.address}</p>}
      </div>
      <div className="flex-shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onEdit(inst)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-white/10" style={{ color: "#9ca3af" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        <button onClick={() => onDelete(inst.id)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-red-500/10" style={{ color: "#ef4444" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ─── Add/Edit form ─────────────────────────────────────────────────────────── */
function InstitutionForm({ initial, onSave, loading }) {
  const [name,    setName]    = useState(initial?.name    || "");
  const [type,    setType]    = useState(initial?.type    || "school");
  const [city,    setCity]    = useState(initial?.city    || "");
  const [address, setAddress] = useState(initial?.address || "");

  const inpStyle = {
    base: "w-full rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none transition-all duration-200",
    bg:   { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" },
    focus: "rgba(139,92,246,0.5)",
  };

  return (
    <div className="space-y-4">
      {/* Type selector */}
      <div>
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2.5">Einrichtungstyp</p>
        <div className="grid grid-cols-2 gap-2">
          {TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setType(t.id)}
              className="flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all duration-200"
              style={type === t.id
                ? { background: t.bg, borderColor: t.color, color: t.color }
                : { background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.06)", color: "#6b7280" }}
            >
              <t.Icon/>
              <div>
                <p className="text-xs font-bold">{t.label}</p>
                <p className="text-[9px] leading-tight opacity-70">{t.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Name */}
      <div>
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">Name <span className="text-red-500">*</span></p>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name der Einrichtung eingeben"
          className={inpStyle.base}
          style={inpStyle.bg}
          onFocus={(e) => { e.currentTarget.style.borderColor = inpStyle.focus; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
        />
      </div>

      {/* City + Address */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">Stadt</p>
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Wien, Graz…"
            className={inpStyle.base}
            style={inpStyle.bg}
            onFocus={(e) => { e.currentTarget.style.borderColor = inpStyle.focus; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
          />
        </div>
        <div>
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">Adresse</p>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Musterstraße 1"
            className={inpStyle.base}
            style={inpStyle.bg}
            onFocus={(e) => { e.currentTarget.style.borderColor = inpStyle.focus; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => onSave({ name, type, city: city || undefined, address: address || undefined })}
        disabled={loading || !name.trim()}
        className="w-full py-3 rounded-xl text-white text-sm font-black transition-all duration-200 disabled:opacity-40 flex items-center justify-center gap-2"
        style={{ background: "linear-gradient(135deg, #7c3aed, #5b21b6)", boxShadow: "0 4px 20px rgba(124,58,237,0.3)" }}
      >
        {loading
          ? <><div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"/><span>Speichere…</span></>
          : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            {initial ? "Änderungen speichern" : "Einrichtung anlegen"}</>
        }
      </button>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────────── */
export default function InstitutionManager() {
  const qc = useQueryClient();
  const [addModal,  setAddModal]  = useState(false);
  const [editInst,  setEditInst]  = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [search,    setSearch]    = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["institutions-all"],
    queryFn: () => api.get("/institutions").then((r) => r.data),
  });
  const institutions = data?.institutions || [];

  const create = useMutation({
    mutationFn: (body) => api.post("/institutions", body),
    onSuccess: () => { toast.success("Einrichtung angelegt"); qc.invalidateQueries({ queryKey: ["institutions-all"] }); setAddModal(false); },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }) => api.put(`/institutions/${id}`, body),
    onSuccess: () => { toast.success("Einrichtung aktualisiert"); qc.invalidateQueries({ queryKey: ["institutions-all"] }); setEditInst(null); },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/institutions/${id}`),
    onSuccess: () => { toast.success("Einrichtung entfernt"); qc.invalidateQueries({ queryKey: ["institutions-all"] }); },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  /* Filter + search */
  const filtered = institutions.filter((i) => {
    const matchType = filterType === "all" || i.type === filterType;
    const matchSearch = !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.city || "").toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  /* Per-type counts */
  const counts = Object.fromEntries(TYPES.map((t) => [t.id, institutions.filter((i) => i.type === t.id).length]));

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-black flex items-center gap-2 text-white">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(139,92,246,0.15)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </span>
            Einrichtungen
          </h1>
          <p className="text-xs text-gray-500 mt-0.5 ml-11">{institutions.length} Einrichtungen registriert</p>
        </div>
        <button
          onClick={() => setAddModal(true)}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-xs font-black transition-all duration-200 flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #7c3aed, #5b21b6)", boxShadow: "0 4px 20px rgba(124,58,237,0.35)" }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Hinzufügen
        </button>
      </div>

      {/* Type overview cards */}
      <div className="grid grid-cols-2 gap-2.5">
        {TYPES.map((t) => (
          <div key={t.id} className="p-3.5 rounded-2xl border flex items-center gap-3" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.05)" }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.bg, color: t.color }}>
              <t.Icon/>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-gray-300 truncate">{t.label}</p>
              <p className="text-lg font-black" style={{ color: t.color }}>{counts[t.id] || 0}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter + search */}
      <div className="space-y-2.5">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Einrichtung suchen…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none transition-all duration-200"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
          {[{ id: "all", label: "Alle" }, ...TYPES].map((t) => (
            <button
              key={t.id}
              onClick={() => setFilterType(t.id)}
              className="flex-shrink-0 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all duration-200"
              style={filterType === t.id
                ? { background: t.color || "rgba(139,92,246,0.15)", borderColor: t.color || "rgba(139,92,246,0.5)", color: t.id === "all" ? "#c4b5fd" : "#fff" }
                : { background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.05)", color: "#6b7280" }}
            >
              {t.label}
              {t.id !== "all" && <span className="ml-1 opacity-60">{counts[t.id] || 0}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center pt-12"><Spinner size="lg"/></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#4b5563" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
          title={search ? "Keine Treffer" : "Noch keine Einrichtungen"}
          desc={search ? `Keine Einrichtung gefunden für "${search}"` : "Füge deine erste Einrichtung hinzu"}
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((inst) => (
            <InstitutionCard
              key={inst.id}
              inst={inst}
              onEdit={(i) => setEditInst(i)}
              onDelete={(id) => {
                if (window.confirm("Einrichtung wirklich entfernen?")) remove.mutate(id);
              }}
            />
          ))}
        </div>
      )}

      {/* Add modal */}
      <Modal open={addModal} onClose={() => setAddModal(false)} title="Einrichtung anlegen">
        <InstitutionForm onSave={(body) => create.mutate(body)} loading={create.isPending}/>
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editInst} onClose={() => setEditInst(null)} title="Einrichtung bearbeiten">
        {editInst && (
          <InstitutionForm
            initial={editInst}
            onSave={(body) => update.mutate({ id: editInst.id, ...body })}
            loading={update.isPending}
          />
        )}
      </Modal>
    </div>
  );
}
